import { useEffect, useState, useCallback } from 'react';
import { adminGet, adminPost, adminPatch } from '../adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Card, Field, Sel, GREEN, mono, StatusPill } from './bfsiShared';

const CHECK_LABELS = {
  QC_LOCKBOX: 'QC + lockbox test', HOME_SERVER: 'Home server', SIM_WHITELIST: 'SIM whitelist',
  CERTIFICATE: 'Certificate', FIRMWARE_SIGNED: 'Firmware signed', CONTRACT: 'Contract', ENVIRONMENT: 'Environment',
};

function ProvisionModal({ device, onClose, onSaved }) {
  const [form, setForm] = useState({
    home_server: device.home_server || '', sim_whitelist_status: device.sim_whitelist_status || 'NOT_REQUESTED',
    sim_whitelist_ticket_ref: '', iot_certificate_id: '',
  });
  const save = async () => {
    try {
      await adminPatch(`/devices/${device.device_id}/provisioning`, form);
      toast.success('Provisioning updated'); onSaved(); onClose();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="provision-modal">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="font-bold mb-4" style={{ color: '#1B1D1C' }}>Provision {device.track0tic_id}</h3>
        <div className="space-y-3">
          <Field label="Home server (MQTT domain)" value={form.home_server} onChange={v => setForm({ ...form, home_server: v })} testId="prov-home-server" />
          <Sel label="SIM whitelist" value={form.sim_whitelist_status} onChange={v => setForm({ ...form, sim_whitelist_status: v })} testId="prov-sim-status"
            options={[['NOT_REQUESTED', 'Not requested'], ['REQUESTED', 'Requested from operator'], ['CONFIRMED', 'Confirmed']]} />
          <Field label="Operator ticket ref" value={form.sim_whitelist_ticket_ref} onChange={v => setForm({ ...form, sim_whitelist_ticket_ref: v })} testId="prov-ticket-ref" />
          <Field label="Device certificate / thing name" value={form.iot_certificate_id} onChange={v => setForm({ ...form, iot_certificate_id: v })} testId="prov-cert-id" />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} data-testid="prov-cancel-btn">Cancel</Button>
          <Button onClick={save} data-testid="prov-save-btn" className="text-white" style={{ background: GREEN }}>Save</Button>
        </div>
      </div>
    </div>
  );
}

function ReleaseModal({ device, tenantId, onClose, onSaved }) {
  const [receiver, setReceiver] = useState('');
  const release = async () => {
    try {
      const r = await adminPost(`/devices/${device.device_id}/release`, { tenant_id: tenantId, receiver_name: receiver });
      toast.success(`${r.released} released · vertical locked to BFSI`); onSaved(); onClose();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="release-modal">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="font-bold mb-2" style={{ color: '#1B1D1C' }}>Release {device.track0tic_id}</h3>
        <p className="text-xs mb-4" style={{ color: '#9a9e96' }}>
          Custody moves to the customer's named receiver. Once with a BFSI customer, the device is
          vertical-locked and cannot go to a non-BFSI customer without a wipe and re-QC.
        </p>
        <Field label="Customer's named receiver" value={receiver} onChange={setReceiver} testId="release-receiver-input" />
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} data-testid="release-cancel-btn">Cancel</Button>
          <Button onClick={release} disabled={!receiver.trim()} data-testid="release-confirm-btn"
            className="text-white" style={{ background: GREEN }}>Release to customer</Button>
        </div>
      </div>
    </div>
  );
}

export default function BfsiProvisioningTab({ tenantId }) {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null); // {type, device}

  const load = useCallback(() => {
    adminGet(`/customers/${tenantId}/provisioning`).then(setData).catch(e => toast.error(e.message));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const requestAction = async (device, kind) => {
    const reason = window.prompt(kind === 'override' ? 'Reason for remote unlock override:' : 'Reason for offline code:');
    if (!reason) return;
    try {
      await adminPost(`/devices/${device.device_id}/${kind === 'override' ? 'override-requests' : 'offline-code-requests'}`, { reason });
      toast.success('Request created — awaiting approval (see Approvals)');
    } catch (e) { toast.error(e.message); }
  };

  const wipe = async (device) => {
    const note = window.prompt('Wipe note (device returns to stock; QC required again):');
    if (!note) return;
    try {
      await adminPost(`/devices/${device.device_id}/wipe`, { note });
      toast.success('Device wiped — re-QC required before next allocation'); load();
    } catch (e) { toast.error(e.message); }
  };

  if (!data) return <div className="text-sm" style={{ color: '#9a9e96' }}>Loading…</div>;

  return (
    <div data-testid="bfsi-provisioning-tab">
      {data.blocked_reason && (
        <div className="rounded-lg border px-4 py-3 mb-5 text-sm font-medium" data-testid="provisioning-blocked-banner"
          style={{ background: '#FBEAE5', borderColor: '#F0C4B5', color: '#B3401F' }}>
          Devices cannot be released: {data.blocked_reason}. Complete Entity &amp; deployment first.
        </div>
      )}
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase" style={{ color: '#9a9e96' }}>
              {['Track0tic ID', 'Checks', 'Home server', 'Vertical lock', 'Status', ''].map(h =>
                <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.devices.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center" style={{ color: '#9a9e96' }}>No lockbox devices in stock</td></tr>
            )}
            {data.devices.map(d => (
              <tr key={d.device_id} className="border-t align-top" style={{ borderColor: '#F0EEE8' }} data-testid={`prov-row-${d.track0tic_id}`}>
                <td className="px-4 py-3 font-medium" style={mono}>{d.track0tic_id}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1 max-w-md">
                    {Object.entries(d.checks).map(([k, ok]) => <StatusPill key={k} ok={ok} label={CHECK_LABELS[k] || k} />)}
                  </div>
                </td>
                <td className="px-4 py-3" style={mono}>{d.home_server || '—'}</td>
                <td className="px-4 py-3">{d.vertical_lock || '—'}</td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={d.all_green ? { background: '#E3F1EC', color: GREEN } : { background: '#FDF3E0', color: '#9a6b12' }}>
                    {d.all_green ? 'Ready to ship' : 'In progress'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1 items-end">
                    <Button size="sm" variant="outline" data-testid={`prov-edit-btn-${d.track0tic_id}`}
                      onClick={() => setModal({ type: 'prov', device: d })} className="h-7 text-xs">Provision</Button>
                    <Button size="sm" data-testid={`prov-release-btn-${d.track0tic_id}`} disabled={!d.all_green || !!data.blocked_reason}
                      onClick={() => setModal({ type: 'release', device: d })}
                      className="h-7 text-xs text-white" style={{ background: GREEN }}>Release</Button>
                    <div className="flex gap-1">
                      <button data-testid={`prov-override-btn-${d.track0tic_id}`} onClick={() => requestAction(d, 'override')}
                        className="text-xs hover:underline" style={{ color: '#6b7069' }}>Unlock override</button>
                      <button data-testid={`prov-offline-btn-${d.track0tic_id}`} onClick={() => requestAction(d, 'offline')}
                        className="text-xs hover:underline" style={{ color: '#6b7069' }}>Offline code</button>
                      <button data-testid={`prov-wipe-btn-${d.track0tic_id}`} onClick={() => wipe(d)}
                        className="text-xs hover:underline" style={{ color: '#B3401F' }}>Wipe</button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="text-xs mt-3" style={{ color: '#9a9e96' }}>
        Lockbox QC adds: solenoid lock/unlock, locked on power loss (fail-secure), tamper switch,
        offline code unlock, lock-state kept after reboot. The home server is pushed at provisioning —
        the device only talks to this customer's MQTT domain.
      </p>
      {modal?.type === 'prov' && <ProvisionModal device={modal.device} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === 'release' && <ReleaseModal device={modal.device} tenantId={tenantId} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
}
